import React, { useRef, useState } from 'react';
import styled from '@emotion/styled';

const Wrapper = styled.div<{
  background: string | null | undefined;
  border?: string;
  disabled?: boolean;
  smallText?: boolean;
  slim?: boolean;
  hover?: boolean;
  marginLeft?: number;
  larger?: boolean;
  column?: boolean;
  indented?: boolean;
}>`
  width: calc(100% - 20px);
  margin: ${(props) => (props.slim ? 5 : 10)}px 10px;
  height: ${(props) => (props.slim ? 48 : props.larger ? 80 : 60)}px;
  display: flex;
  padding: 0 10px;
  align-items: center;
  flex-direction: ${(props) => (props.column ? 'column' : 'row')};
  justify-content: center;
  cursor: pointer;
  box-sizing: border-box;
  color: ${(props) => props.color || '#fff'};
  text-align: center;
  font-size: ${(props) => (props.smallText ? 13 : 17)}px;
  opacity: ${(props) => (props.disabled ? '0.5' : '1')};
  margin-left: ${(props) => props.marginLeft};
  ${(props) => props.border && `border-bottom: 10px solid ${props.border}`};
  border-radius: 3px;
  background: ${(props) => props.background || '#465fe6'};
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.15);
  transition: all 0.1s ease-out;
  ${({ hover }) =>
    hover &&
    `
    transform: scale(0.97);
    `}

  ${({ indented }) =>
    indented &&
    `
    transform: scale(0.97);
    box-shadow: inset 0px 1px 3px rgba(0, 0, 0, 0.2);

    `}
`;

interface Props {
  primary?: boolean;
  onClick?: () => void;
  onLongPress?: () => void;
  onLongPressEnd?: () => void;
  onTouchStart?: () => void;
  onTouchEnd?: () => void;
  background?: string;
  smallText?: boolean;
  slim?: boolean;
  disabled?: boolean;
  style?: object;
  color?: string;
  larger?: boolean;
  column?: boolean;
  indented?: boolean;
}

const Button: React.FunctionComponent<Props> = ({
  children,
  primary,
  onClick,
  onTouchStart,
  onTouchEnd,
  background,
  onLongPress,
  ...props
}) => {
  const [hover, setHover] = useState(false);
  const [touching, setTouching] = useState(false);
  const timeoutRef = useRef<number | null>(null);

  const backgroundColor = primary ? '#37de61' : background;

  const handleLongPress = () => {
    setTouching(true);

    timeoutRef.current = setTimeout(() => {
      onLongPress && onLongPress();
      setTouching(false);
    }, 500);
  };

  const handleLongPressEnd = () => {
    if (touching) {
      clearTimeout(timeoutRef.current!);
      setTouching(false);
    }
  };

  return (
    <Wrapper
      hover={hover}
      background={backgroundColor}
      {...props}
      onTouchStart={() => {
        if (props.disabled) {
          return;
        }

        setHover(true);

        if (onTouchStart) {
          onTouchStart();
        }

        if (onLongPress) {
          handleLongPress();
        }
      }}
      onTouchEnd={() => {
        setHover(false);

        if (onTouchEnd) {
          onTouchEnd();
        }

        if (onLongPress) {
          handleLongPressEnd();
        }
      }}
      onClick={() => {
        if (!props.disabled && onClick) {
          onClick();
        }
      }}
      style={props?.style}>
      {children}
    </Wrapper>
  );
};

Button.displayName = 'Button';

export default Button;
